import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  SAVE_CARGA_POTENCIA_PADRAO_KW,
  calcularControleDemanda,
  calcularExemploControleDemanda,
  formatarKw,
  type SaveCargaEntrada,
} from "@/utils/saveCargaPropostaUtils";

type SaveCargaEstudoFieldProps = {
  incluirEstudo: boolean;
  entrada: SaveCargaEntrada;
  onIncluirEstudoChange: (incluir: boolean) => void;
  onEntradaChange: (entrada: SaveCargaEntrada) => void;
};

function CampoKw({
  id,
  label,
  value,
  onChange,
  opcional = false,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (n: number) => void;
  opcional?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        step={0.1}
        value={value === 0 ? "" : value}
        placeholder={opcional ? "Opcional" : "0"}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          onChange(Number.isFinite(n) && n >= 0 ? n : 0);
        }}
      />
    </div>
  );
}

export function SaveCargaEstudoField({
  incluirEstudo,
  entrada,
  onIncluirEstudoChange,
  onEntradaChange,
}: SaveCargaEstudoFieldProps) {
  const exemplo = calcularExemploControleDemanda();
  const calculoEstudo = calcularControleDemanda(entrada);
  const patch = (partial: Partial<SaveCargaEntrada>) =>
    onEntradaChange({ ...entrada, ...partial });

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-0.5">
          <Label htmlFor="save-incluir-estudo-carga" className="text-base">
            Incluir dados do estudo de viabilidade de carga
          </Label>
          <p className="text-sm text-muted-foreground">
            Ligado, o PDF usa as medições deste condomínio. Desligado, entra o exemplo
            ilustrativo de controle de demanda (50 pontos × 7,4 kW, pico 80 kW, mínimo 10 kW,
            ramal 150 kW).
          </p>
        </div>
        <Switch
          id="save-incluir-estudo-carga"
          checked={incluirEstudo}
          onCheckedChange={(checked) => {
            onIncluirEstudoChange(checked);
            if (checked && (Number(entrada.potenciaPontoKw) || 0) <= 0) {
              patch({ potenciaPontoKw: SAVE_CARGA_POTENCIA_PADRAO_KW });
            }
          }}
        />
      </div>

      {incluirEstudo ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CampoKw
              id="save-carga-demanda-max"
              label="Demanda máxima medida (kW)"
              value={entrada.demandaMaximaKw}
              onChange={(demandaMaximaKw) => patch({ demandaMaximaKw })}
            />
            <CampoKw
              id="save-carga-demanda-ramal"
              label="Demanda do ramal / capacidade (kW)"
              value={entrada.demandaRamalKw}
              onChange={(demandaRamalKw) => patch({ demandaRamalKw })}
            />
            <div className="space-y-2">
              <Label htmlFor="save-carga-qtd-pontos">Pontos de recarga previstos</Label>
              <Input
                id="save-carga-qtd-pontos"
                type="number"
                min={1}
                step={1}
                value={entrada.qtdPontos}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  patch({ qtdPontos: Number.isFinite(n) && n >= 1 ? n : 1 });
                }}
              />
            </div>
            <CampoKw
              id="save-carga-potencia-ponto"
              label="Potência por ponto (kW)"
              value={entrada.potenciaPontoKw}
              onChange={(potenciaPontoKw) => patch({ potenciaPontoKw })}
            />
            <CampoKw
              id="save-carga-demanda-min"
              label="Demanda mínima medida (kW)"
              value={entrada.demandaMinimaKw}
              onChange={(demandaMinimaKw) => patch({ demandaMinimaKw })}
              opcional
            />
            <CampoKw
              id="save-carga-demanda-media"
              label="Demanda média (kW)"
              value={entrada.demandaMediaKw}
              onChange={(demandaMediaKw) => patch({ demandaMediaKw })}
              opcional
            />
            <CampoKw
              id="save-carga-demanda-verao"
              label="Demanda de pico no verão (kW)"
              value={entrada.demandaVeraoKw}
              onChange={(demandaVeraoKw) => patch({ demandaVeraoKw })}
              opcional
            />
          </div>
          {calculoEstudo.demandaMaximaKw > 0 && calculoEstudo.demandaRamalKw > 0 ? (
            <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
              <p>
                Sem sistema inteligente: {formatarKw(calculoEstudo.somaSemControleKw)}
                {calculoEstudo.estouraRamalSemControle ? " (acima do limite)" : ""}. Com
                sistema:{" "}
                {calculoEstudo.temFaixaDisponivel
                  ? `${calculoEstudo.recargasSimultaneas} a ${calculoEstudo.recargasSimultaneasMax} recargas simultâneas (${formatarKw(calculoEstudo.margemKw)} a ${formatarKw(calculoEstudo.margemDisponivelMaxKw)} disponíveis).`
                  : `${calculoEstudo.recargasSimultaneas} recargas simultâneas na margem de ${formatarKw(calculoEstudo.margemKw)}.`}
              </p>
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          O PDF mostrará o exemplo: {exemplo.qtdPontos} pontos × {formatarKw(exemplo.potenciaPontoKw)}{" "}
          = {formatarKw(exemplo.cargaTotalSemControleKw)}; pico {formatarKw(exemplo.demandaMaximaKw)};
          mínimo {formatarKw(exemplo.demandaMinimaKw)}; ramal {formatarKw(exemplo.demandaRamalKw)};
          sem sistema {formatarKw(exemplo.somaSemControleKw)} (acima do limite); com sistema{" "}
          {formatarKw(exemplo.margemKw)} a {formatarKw(exemplo.margemDisponivelMaxKw)} disponíveis (
          {exemplo.recargasSimultaneas} a {exemplo.recargasSimultaneasMax} recargas simultâneas). Os
          números não representam medições deste condomínio.
        </p>
      )}
    </div>
  );
}
